/**
 * Text colours for the semantic tones the EhrSync chips and labels use. Tones
 * whose palette main is too light for text are darkened in light mode, and too
 * dark ones lightened in dark mode, so every chip's text reaches 4.5:1 on its
 * own 14% tint.
 */
import type { Theme } from '@mui/material/styles';
import { darken, lighten } from '@mui/material/styles';

/** Semantic tone of a chip or label. `grey` maps to secondary text. */
export type EhrSyncTone =
  'success' | 'warning' | 'error' | 'grey' | 'teal' | 'orange';

// 0.5 gives about 5.4:1 against a 14% warning tint on white paper. The
// palette's `warning.dark` (#FD7608) is orange and fails the same check.
const LIGHT_WARNING_DARKEN = 0.5;

// Ratios on a 14% tint over the paper colour: light mode success 5.4:1, error
// 5.3:1, teal 5.8:1, orange 5.5:1; dark mode success 5.3:1, error 4.9:1. The
// other mains already pass in that mode.
const LIGHT_DARKEN: Partial<Record<EhrSyncTone, number>> = {
  warning: LIGHT_WARNING_DARKEN,
  teal: 0.45,
  orange: 0.45,
  success: 0.4,
  error: 0.2,
};
const DARK_LIGHTEN: Partial<Record<EhrSyncTone, number>> = {
  success: 0.2,
  error: 0.4,
};

/**
 * Text colour for a tone. Callers SHOULD keep using `palette[tone].main` for
 * tint backgrounds so the tint does not darken with the text.
 *
 * @param theme - the active MUI theme
 * @param tone - the semantic tone to colour
 * @returns a CSS colour string for text in that tone
 */
export function toneTextColor(theme: Theme, tone: EhrSyncTone): string {
  if (tone === 'grey') {
    return theme.palette.text.secondary;
  }
  const main = theme.palette[tone].main;
  if (theme.palette.mode === 'light') {
    const amount = LIGHT_DARKEN[tone];
    return amount === undefined ? main : darken(main, amount);
  }
  const amount = DARK_LIGHTEN[tone];
  return amount === undefined ? main : lighten(main, amount);
}

/**
 * Base colour for a tone's tint background, before alpha is applied.
 *
 * @param theme - the active MUI theme
 * @param tone - the semantic tone to colour
 * @returns a CSS colour string to pass to `alpha()`
 */
export function toneTintColor(theme: Theme, tone: EhrSyncTone): string {
  return tone === 'grey'
    ? theme.palette.text.secondary
    : theme.palette[tone].main;
}
